import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {type OperationsActorServiceInfo} from "@/api/enterprise-gateway-api.proxy.ts";
import * as yup from "yup";
import {useForm} from "vee-validate";
import {useOrganisationStore} from "@/app/stores/organisation-store.ts";
import {storeToRefs} from "pinia";
import {Button, Dialog, InputText} from 'primevue';
import AegCancelButton from '@/app/admin/components/aeg-cancel-button/aeg-cancel-button.vue';

export const emailValidationSchema = yup.object({
    email: yup.string().email('Ongeldig e-mailadres').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, { message: 'Ongeldig e-mailadres', excludeEmptyString: true }).required('E-mailadres is verplicht'),
    emailCc: yup.string().email('Ongeldig e-mailadres').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, { message: 'Ongeldig e-mailadres', excludeEmptyString: true }).notRequired()
});

export interface EmailForm {
    email: string;
    emailCc: string;
}

export default defineComponent({
    name: 'AegEditActorMails',
    components: {AegCancelButton, Button, Dialog, InputText },
    props: {
        visible: { type: Boolean, default: false },
        actor: {
            type: Object as PropType<OperationsActorServiceInfo>,
            required: true
        }
    },
    emits: ['on-save', 'update:visible'],
    setup: function (props, {emit}) {
        const actor = ref<OperationsActorServiceInfo>(props.actor);
        const organisationStore = useOrganisationStore();
        const {savingEmails } = storeToRefs(organisationStore);
        const isSending = ref<boolean>(false);

        const visibleModel = computed({
            get: () => props.visible,
            set: (value) => emit('update:visible', value)
        });

        const {defineField, meta, errors, handleSubmit} = useForm<EmailForm>({
            validationSchema: emailValidationSchema,
            initialValues: {
                email: actor.value.emails?.[0],
                emailCc: actor.value.emails && actor.value.emails?.length > 1 ? actor.value.emails[1] : ''
            },
            validateOnMount: false,
            keepValuesOnUnmount: false,
        });

        const [email, emailProps] = defineField('email');
        const [emailCc, emailCcProps] = defineField('emailCc');

        const handleFormSubmit = async () => {
            const emailsToSave = [email.value];
            if(emailCc.value) {
                emailsToSave.push(emailCc.value);
            }
            await organisationStore.saveEmails(actor.value.actorId!, actor.value.service!, emailsToSave);
            emit('on-save', {
                emails: emailsToSave
            });
            onCloseModal();
        };

        const onCloseModal = () => {
            visibleModel.value = false;
        }

        const disableSubmit = computed((): boolean => {
            return savingEmails.value || !meta.value.valid;
        });

        const actorChanged = async (newActor: OperationsActorServiceInfo) => {
            if(!actor.value) return;
            actor.value = newActor;
        };

        const title = computed(() => {
            if(!actor.value) return;
            return actor.value?.emails?.length == 1 ?
                `${actor.value.service} e-mail aanpassen` :
                `${actor.value.service} e-mails aanpassen`;
        });

        const onSubmit = handleSubmit(handleFormSubmit);

        watch(() => props.actor, actorChanged, {immediate: true, deep: true});

        return {
            visibleModel,
            savingEmails,
            title,
            actor,
            email,
            emailProps,
            emailCc,
            emailCcProps,
            disableSubmit,
            isSending,
            errors,
            onSubmit,
            onCloseModal
        }
    }
});
